import React, { useState, useEffect } from "react";
import { Button, Form, Spinner, Alert } from "react-bootstrap";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from 'yup';
import axios from 'axios';
import { userDocumentUpload } from "../../Common/data/UserDocumentData";
import { getUserProfile } from "../../Common/data/getUserProfile";  
import { getNigerianUniversities } from "../../Common/data/getNigerianUniversities";
import { UserDocumentData } from "../../Common/data/UserDocumentData";
import "../../assets/scss/custom-colors.scss";

interface University {
    name: string;
    website: string;
}

const DocumentSubmissionForm = () => {
    const initialValues: UserDocumentData  = {
        highestLevelEducation: '',
        institutionName: '',
        jobTitle: '',
        workExperience: '',
        cvResume: undefined as File | undefined,
    };

    const validationSchema = Yup.object().shape({
        highestLevelEducation: Yup.string().required("Required"),
        institutionName: Yup.string().required("Required"),
        jobTitle: Yup.string().required("Required"),
        workExperience: Yup.string().required("Required"),
        cvResume: Yup.mixed()
            .required("Resume/CV is required")
            .test(
                "fileSize",
                "File size is too large. Max size is 2MB.",
                (value) => !value || (value instanceof File && value.size <= 2097152)
            )
            .test(
                "fileFormat",
                "Unsupported Format. Please upload PDF or DOCX.",
                (value) => !value || (value instanceof File && ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(value.type))
            ),
            });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [responseMessage, setResponseMessage] = useState("");
    const [showAlert, setShowAlert] = useState(false);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<"none" | "pending" | "verified" | "rejected">("none");
    const [universities, setUniversities] = useState<University[]>([]);
    const [otherInterest, setOtherInterest] = useState('');
    const [selectedInterest, setSelectedInterest] = useState('');
    const [cvRequired, setCvRequired] = useState(true);


    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const userData = await getUserProfile();
                // Check for status values: verified, pending, or rejected
                if (userData.results.bioDataStatus === "pending") {
                    setStatus("pending");
                } else if (userData.results.bioDataStatus === "verified") {
                    setStatus("verified");
                } else if (userData.results.bioDataStatus === "rejected") {
                    setStatus("rejected");
                } else {
                    setStatus("none");
                }
                setLoading(false);
            } catch (error: any) {
                console.error("Error fetching user profile:", error.message);
                setLoading(false);
            }
        };

        const fetchUniversities = async () => {
            try {
                const universitiesData = await getNigerianUniversities();
                console.log("universities", universitiesData);
                // Access universities array from universitiesData.data.universities
                setUniversities(universitiesData.data.universities || []);
            } catch (error: any) {
                console.error("Error fetching Nigerian universities:", error.message);
            }
        };

        fetchUserProfile();
        fetchUniversities();
    }, []);

    const handleSubmit = async (values: typeof initialValues, { setSubmitting, resetForm }: { setSubmitting: Function, resetForm: Function }) => {
        console.log("Attempting to submit:", values);
        setIsSubmitting(true);
    
        try {
            console.log("Submitting values:", values);
            const response = await userDocumentUpload(values);
            setResponseMessage("Your bio-data submission has been successful. It is currently under review, and approval will be granted accordingly.");
            setShowAlert(true);
            resetForm();
        } catch (error: any) {
            console.error('Error uploading document:', error.message);
            setResponseMessage(error.message);
            setShowAlert(true);
        } finally {
            setIsSubmitting(false);
            setSubmitting(false);
        }
    };
    
    const handleInterestChange = (e: any, setFieldValue: any) => {
        const value = e.target.value;
        setSelectedInterest(value);
        setFieldValue('jobTitle', value);
        if (value !== "Others") {
            setOtherInterest(''); 
        }
    };

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>, formikHelpers: any) => {
        event.preventDefault();
        
        const { isValid } = formikHelpers;
    
        if (!isValid) {
            return; 
        }
    
        await handleSubmit(formikHelpers.values, formikHelpers);
    };    

    if (loading) {
        return <Spinner animation="border" variant="primary" />;
    }

    return (
        <>
            <h5 className="mb-4 custom-text-primary">Document Submission</h5>
            {status === "pending" ? (
                <Alert variant="info">Your form has been submitted pending approval.</Alert>
            ) : status === "verified" ? (
                <Alert variant="success">Your bio data has been verified. You can now go to the next phase.</Alert>
            ) : status === "rejected" ? (
                <Alert variant="danger">Your bio data has been rejected.</Alert>
            ) : (
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    validateOnChange={true}
                    validateOnBlur={true}
                >
                    {({ isSubmitting, handleChange, setFieldValue, values, setSubmitting, resetForm, isValid}) => (
                        <Form onSubmit={(event) => {
                            event.preventDefault();
                            handleSubmit(values, { setSubmitting, resetForm });
                          }}>
                            <div className="mb-12">
                                <h6 className="custom-text-primary">Education Background</h6>
                                <hr className="bg-primary" style={{ marginBottom: "25px" }}/>
                                <div className="row" style={{ marginBottom: "25px" }}>
                                    <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label>Highest Level of Education <span style={{ color: 'red' }}>*</span></Form.Label>
                                            <Form.Select name="highestLevelEducation" value={values.highestLevelEducation} onChange={handleChange} aria-label="Highest Level of Education">
                                                <option value="">Select...</option>
                                                <option value="High School">High School</option>
                                                <option value="Bachelor's Degree">Bachelor's Degree</option>
                                                <option value="Master's Degree">Master's Degree</option>
                                            </Form.Select>
                                            <ErrorMessage name="highestLevelEducation" component="div" className="text-danger" />
                                        </Form.Group>
                                    </div>

                                    <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label>Institution Name <span style={{ color: 'red' }}>*</span></Form.Label>
                                            <Form.Select name="institutionName" value={values.institutionName} onChange={handleChange} aria-label="Institution Name">
                                                <option value="">Select...</option>
                                                {universities.map((university, index) => (
                                                    <option key={index} value={university.name}>{university.name}</option> // Use university.name
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                </div>
                                <div className="row" style={{ marginBottom: "25px" }}>
                                <div className="col-md-6">
                                <Form.Group>
                                                <Form.Label>Area of Interest <span style={{ color: 'red' }}>*</span></Form.Label>
                                                <Field
                                                    as="select"
                                                    name="jobTitle"
                                                    className="form-control"
                                                    value={selectedInterest}
                                                    onChange={(e: any) => handleInterestChange(e, setFieldValue)}
                                                >
                                                    <option value="">Select an Area of Interest</option>
                                                    <option value="Web Development">Web Development</option>
                                                    <option value="Data Science">Data Science</option>
                                                    <option value="Machine Learning">Machine Learning</option>
                                                    <option value="Artificial Intelligence">Artificial Intelligence</option>
                                                    <option value="Cybersecurity">Cybersecurity</option>
                                                    <option value="Cloud Computing">Cloud Computing</option>
                                                    <option value="Mobile App Development">Mobile App Development</option>
                                                    <option value="Blockchain Technology">Blockchain Technology</option>
                                                    <option value="DevOps">DevOps</option>
                                                    <option value="User Experience (UX) Design">User Experience (UX) Design</option>
                                                    <option value="Digital Marketing">Digital Marketing</option>
                                                    <option value="Python Programming">Python Programming</option>
                                                    <option value="Java Programming">Java Programming</option>
                                                    <option value="Front-End Development">Front-End Development</option>
                                                    <option value="Back-End Development">Back-End Development</option>
                                                    <option value="Full Stack Development">Full Stack Development</option>
                                                    <option value="Game Development">Game Development</option>
                                                    <option value="Ethical Hacking">Ethical Hacking</option>
                                                    <option value="Agile Project Management">Agile Project Management</option>
                                                    <option value="Internet of Things (IoT)">Internet of Things (IoT)</option>
                                                    <option value="Augmented Reality (AR) Development">Augmented Reality (AR) Development</option>
                                                    <option value="Virtual Reality (VR) Development">Virtual Reality (VR) Development</option>
                                                    <option value="Data Visualization">Data Visualization</option>
                                                    <option value="SQL and Database Design">SQL and Database Design</option>
                                                    <option value="RESTful API Development">RESTful API Development</option>
                                                    <option value="Cloud Architecture">Cloud Architecture</option>
                                                    <option value="IT Support and Networking">IT Support and Networking</option>
                                                    <option value="TensorFlow for Deep Learning">TensorFlow for Deep Learning</option>
                                                    <option value="Natural Language Processing (NLP)">Natural Language Processing (NLP)</option>
                                                    <option value="Computer Graphics">Computer Graphics</option>
                                                    <option value="Software Testing and Quality Assurance">Software Testing and Quality Assurance</option>
                                                    <option value="Systems Administration">Systems Administration</option>
                                                    <option value="E-commerce Development">E-commerce Development</option>
                                                    <option value="Responsive Web Design">Responsive Web Design</option>
                                                    <option value="Git and Version Control">Git and Version Control</option>
                                                    <option value="Business Intelligence">Business Intelligence</option>
                                                    <option value="Microservices Architecture">Microservices Architecture</option>
                                                    <option value="Others">Others</option>
                                                </Field>
                                                <ErrorMessage name="jobTitle" component="div" className="text-danger" />
                                            </Form.Group>
                                            {selectedInterest === "Others" && (
                                                <Form.Group>
                                                    <Form.Label>Please specify your Area of Interest</Form.Label>
                                                    <Field
                                                        name="otherJobTitle"
                                                        className="form-control"
                                                        value={otherInterest}
                                                        onKeyDown={(e: any) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                        onChange={(e: any) => {
                                                            setOtherInterest(e.target.value);
                                                            setFieldValue('jobTitle', e.target.value); // Set the job title to the value of the other interest
                                                        }}
                                                        onBlur={(e: any) => {
                                                            if (e.target.value.trim() === '') {
                                                                setOtherInterest('');
                                                                setFieldValue('jobTitle', ''); // Reset if input is empty
                                                            } else {
                                                                setFieldValue('jobTitle', e.target.value); 
                                                            }
                                                        }}
                                                    />
                                                    <ErrorMessage name="otherJobTitle" component="div" className="text-danger" />
                                                </Form.Group>
                                                )}
                                            </div>
                                        <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label>Work Experience <span style={{ color: 'red' }}>*</span></Form.Label>
                                            <Field as="select" name="workExperience" value={values.workExperience} className="form-control">
                                                <option value="">Select Work Experience</option>
                                                <option value="0-1">Entry-Level (0-1 years)</option>
                                                <option value="1-3">Junior Level (1-3 years)</option>
                                                <option value="3-5">Mid-Level (3-5 years)</option>
                                                <option value="5-10">Senior Level (5-10 years)</option>
                                                <option value="10+">Executive Level (10+ years)</option>
                                            </Field>
                                            <ErrorMessage name="workExperience" component="div" className="text-danger" />
                                        </Form.Group>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h6 className="custom-text-primary">Document Submission</h6>
                                <hr className="bg-primary" />
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label>Upload Resume or CV <span style={{ color: 'red' }}>*</span></Form.Label>
                                            <Form.Control 
                                                type="file" 
                                                name="cvResume" 
                                                onChange={(event) => {
                                                    const target = event.currentTarget as HTMLInputElement;
                                                    if (target.files) {
                                                        setFieldValue("cvResume", target.files[0]);
                                                    }
                                                }} 
                                            />
                                            <ErrorMessage name="cvResume" component="div" className="text-danger" />
                                        </Form.Group>
                                    </div>
                                </div>
                                {showAlert && (
                                    <Alert variant="success" onClose={() => setShowAlert(false)} dismissible>
                                        {responseMessage}
                                    </Alert>
                                )}
                                <div className="text-end">
                                <Button type="submit" disabled={isSubmitting || !values.highestLevelEducation || !values.institutionName || !values.jobTitle || !values.workExperience || !values.cvResume}>
                                Submit
                               </Button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
        </>
    );
};

export default DocumentSubmissionForm;
